import React from "react";
import { Box, Flex, Text, Spinner, Icon } from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import { useInspectorTokens } from "./inspectorUi";

const SCROLL_CSS = {
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "rgba(112,126,174,0.35)", borderRadius: "10px" },
};

/**
 * Fixed-height inspector container: a light Horizon card pinned under the navbar
 * with a non-scrolling header slot and an internally scrolling body, so the
 * whole page never has to scroll to read a record's details.
 * @param {Object} props
 * @param {React.ReactNode} [props.header] - Sticky header content (stays put)
 * @param {React.ReactNode} props.children - Scrollable body content
 * @returns {React.ReactElement}
 */
export function InspectorShell({ header, children }) {
    return (
        <Card p="0" overflow="hidden" display="flex" flexDirection="column"
            h={{ base: "auto", md: "calc(100vh - 140px)" }}
            maxH={{ base: "78vh", md: "calc(100vh - 140px)" }}
            position={{ md: "sticky" }} top={{ md: "104px" }}>
            {header && <Box flexShrink={0}>{header}</Box>}
            <Box flex="1" minH="0" overflowY="auto" p="16px" css={SCROLL_CSS}>{children}</Box>
        </Card>
    );
}

/**
 * Centered placeholder used for the empty / loading / error states, wrapped in
 * the same shell so the right column keeps a constant footprint.
 * @param {Object} props - { icon, label, spin, tone }
 * @returns {React.ReactElement}
 */
export function InspectorCentered({ icon, label, spin, tone }) {
    const t = useInspectorTokens();
    return (
        <InspectorShell>
            <Flex h="100%" direction="column" align="center" justify="center" gap="12px" textAlign="center" px="24px">
                {spin
                    ? <Spinner color={t.accent} size="lg" thickness="3px" />
                    : <Icon as={icon} boxSize="42px" color={tone || t.label} opacity={0.6} />}
                <Text color={t.label} fontSize="0.9rem">{label}</Text>
            </Flex>
        </InspectorShell>
    );
}
